/** @format */

import React from 'react';
import update from 'immutability-helper';
import SuperAgent from 'superagent';
import {
    COLOR_BLACK_ONLY,
    COLOR_CAPATIBILITY_MATRIX,
    COLOR_DOUBLE_SIDED,
    DESIGN_QUOTER_STEP_LANDING,
    DESIGN_QUOTER_STEP_QUOTER,
    LAMINATE_NO_LAMINATE,
    NO_LAMINATE_SUBSTRATES,
    PRODUCT_TYPE_CARTON,
    PRODUCT_TYPE_GRAPHIC_DESIGN,
    PRODUCT_TYPE_LABEL,
    SUBSTRATE_BACKING_DEFAULTS,
    SUBSTRATE_BLACK_VELLUM,
    SUBSTRATE_KRAFT_PAPER
} from 'frontier-constants';

import {QuoteContext, QuoterContextDefault} from 'frontier-quoter';
import {AccountContextProvider, calcDiameterFromQty, calcQtyFromDiameter} from 'frontier-utilities';
import {validateOrderWithErrors} from 'frontier/ds/quoter/functions/validate';
import {getMaxRollDiameterForWidth} from 'frontier/utilities/roll_min_max_validator';
import {clearThicknessCache} from 'frontier/utilities/substrate_thicknesses';
import {
    mapCartToState,
    mapOrderToState,
    mapQuoteToState
} from 'frontier/components/data_maps/orders/quoter';

/**
 * WithDataHandler is the HOC that manages updating the master order state for the quote
 * When the quote is started it can pass down the starting state
 * When the quote is complete, it handles saving the data to the order
 * @param ContainedComponent
 * @returns {{}}
 * @constructor
 */

const STATUS_100 = 100;
export default ContainedComponent => {
    return class withQuoteDataHandler extends React.Component {
        constructor(props) {
            super(props);

            this.visibilityRef = React.createRef();

            let state = QuoterContextDefault;
            this.state = update(state, {
                lastUpdate: {$set: Date.now()},
                size: {$set: props.orderPreload.size},
                order: {$set: props.orderPreload},
                cart: {$set: props.cartPreload},
                substrates: {$set: props.substrates},
                laminates: {$set: props.laminates},
                formFactors: {$set: props.formFactors},
                processingTimes: {$set: props.processingTimes},
                filterableSubstrates: {$set: props.filterableSubstrates},
                substrateAttributes: {$set: props.substrateAttributes},
                substrateCategories: {$set: props.substrateCategories},
                isCartOpen: {$set: props.isCartOpen},
                isAddingNewOrder: {$set: false}, // Are we adding a new order
                showErrorsFromCart: {$set: false},
                showAddedToCart: {$set: false},
                designQuoterStep: {
                    $set: props.orderPreload?.graphic_design_order_details?.design_type_id
                        ? DESIGN_QUOTER_STEP_QUOTER
                        : DESIGN_QUOTER_STEP_LANDING
                }
            });
        }

        /**
         * On mount, load an order entity
         * If the user has an order in their local storage, use that
         * If not, load a new entity
         */
        async componentDidMount() {
            // // call the update inside a nested function so SuperAgent returns a request and not a promise
            // // the request can be aborted if needed, the promise cannot
            // await this.debounceQuoteUpdate();
            // preloadAllSizes();
            this.visibilityRef.current = Date.now();
            window.addEventListener('blur', this.handleVisibilityBlur, false);
            window.addEventListener('focus', this.handleVisibilityFocus, false);
            const response = await SuperAgent.get('/account/users/current-user').set(
                'accept',
                'application/json'
            );

            if (response.body.user && response.body.user.id && !response.body.user.is_guest) {
                this.setState({isLoggedIn: true});
            } else {
                this.setState({isLoggedIn: false});
            }
        }

        componentWillUnmount() {
            window.removeEventListener('blur', this.handleVisibilityBlur);
            window.removeEventListener('focus', this.handleVisibilityFocus);
        }

        componentDidUpdate(prevProps, prevState) {
            if (typeof this.waitforpeace !== 'undefined') {
                clearTimeout(this.waitforpeace);
            }
            if (typeof this.previousQty === 'undefined') {
                this.previousQty = prevState.order.total_qty;
            }

            this.waitforpeace = setTimeout(() => {
                if (
                    this.previousQty < this.state.order.total_qty &&
                    this.state.order.total_qty >= 250
                ) {
                    if (typeof window.dataLayer !== 'undefined') {
                        window.dataLayer.push({
                            event: 'qtyOver250'
                        });
                    }
                }

                delete this.previousQty;
            }, 5000);
        }

        /**
         * Alias
         * @type {QuoterAppDataHandler.handleChange}
         */
        handleUpdate = this.handleChange;

        handleChange = (updateTarget, {skipUpdate = false} = {}) => {
            // Check if width is being updated and enforce roll constraints
            if (updateTarget.order?.width?.$set !== undefined) {
                const newWidth = updateTarget.order.width.$set;
                const currentRollDiameter = this.state.order.roll_diameter_max;
                const currentRollQty = this.state.order.roll_qty_max;

                const maxAllowedDiameter = getMaxRollDiameterForWidth(
                    newWidth,
                    this.state.order.product_type_id
                );

                // Auto-adjust roll diameter if it exceeds the limit
                if (currentRollDiameter > 0 && currentRollDiameter > maxAllowedDiameter) {
                    updateTarget = {
                        ...updateTarget,
                        order: {
                            ...updateTarget.order,
                            roll_diameter_max: {$set: maxAllowedDiameter},
                            roll_max: {$set: maxAllowedDiameter}
                        }
                    };
                }

                // Auto-adjust roll quantity if it would result in a diameter exceeding the limit
                if (currentRollQty > 0) {
                    const {
                        order: {
                            length,
                            substrate_id: substrateId,
                            is_high_opacity_white: isHighOpacityWhite
                        }
                    } = this.state;

                    // Only calculate if we have valid parameters
                    if (length > 0 && substrateId) {
                        const resultingDiameter = calcDiameterFromQty({
                            qty: currentRollQty,
                            length,
                            orderId: this.state.order.id,
                            substrateId,
                            laminateId: this.state.order.laminate_id,
                            isHighOpacityWhite
                        });

                        if (resultingDiameter > maxAllowedDiameter) {
                            const maxAllowedQty = calcQtyFromDiameter({
                                diameter: maxAllowedDiameter,
                                length,
                                orderId: this.state.order.id,
                                substrateId,
                                laminateId: this.state.order.laminate_id,
                                isHighOpacityWhite
                            });

                            updateTarget = {
                                ...updateTarget,
                                order: {
                                    ...updateTarget.order,
                                    roll_qty_max: {$set: maxAllowedQty},
                                    roll_max: {$set: maxAllowedQty}
                                }
                            };
                        }
                    }
                }
            }

            this.setState(
                state => update(state, updateTarget),
                () => {
                    if (!skipUpdate) {
                        this.debounceQuoteUpdate();
                    }
                }
            );
        };

        /**
         * Handle changes to the graphic_design_order_details child object.
         *
         */
        handleDesignOrderChange = updateTarget => {
            this.handleChange({order: {graphic_design_order_details: updateTarget}});
        };

        /**
         * Handle changing the design quoter's current step.
         */
        handleSetDesignQuoterStep = step => {
            window.scrollTo(0, 0);
            this.handleChange({designQuoterStep: {$set: step}});
        };

        handleVisibilityBlur = () => {
            this.visibilityRef.current = Date.now();
        };

        handleVisibilityFocus = () => {
            if ((Date.now() - this.visibilityRef.current) / 1000 > 120) {
                // You were away for more than 2 minutes, reset the quote
                // We are now visible, make sure we are still valid
                this.debounceQuoteUpdate({skipSendData: true});
            }

            this.visibilityRef.current = Date.now();
        };

        handleLogin = ({isLoggedIn = false}) => {
            this.handleChange({isLoggedIn: isLoggedIn});
        };

        /**
         * Handle some side effects when the material is switched.
         */
        handleChangeMaterial = substrate_id => {
            const updateTarget = {substrate_id: {$set: substrate_id}};
            if (NO_LAMINATE_SUBSTRATES.includes(substrate_id)) {
                updateTarget.laminate_id = {$set: LAMINATE_NO_LAMINATE};
            }

            // Deselect reverse print if switched to a non-applicable material
            if (!COLOR_CAPATIBILITY_MATRIX[COLOR_DOUBLE_SIDED].includes(substrate_id)) {
                updateTarget.is_reverse_print = {$set: false};
            }

            // Set our default backing
            if (SUBSTRATE_BACKING_DEFAULTS[substrate_id]) {
                updateTarget.substrate_backing_id = {
                    $set: SUBSTRATE_BACKING_DEFAULTS[substrate_id]
                };
            }

            // Clear thickness cache when substrate changes
            if (this.state.order.id) {
                clearThicknessCache(this.state.order.id);
            }

            // Pre-select High Opacity White for some materials
            const preselectHighOpacityWhite = [SUBSTRATE_KRAFT_PAPER, SUBSTRATE_BLACK_VELLUM];
            if (
                preselectHighOpacityWhite.includes(substrate_id) &&
                this.state.order.color_id !== COLOR_BLACK_ONLY
            ) {
                updateTarget.is_high_opacity_white = {$set: true};
                updateTarget.is_high_opacity_black = {$set: false};
            } else {
                updateTarget.is_high_opacity_white = {$set: false};
            }

            this.handleChange({order: updateTarget});
        };

        /**
         * Don't submit a quote to the server when certain client-side validations are
         * failing to prevent overwriting user input with incomplete data derived by the
         * server.
         */
        shouldSubmitChange = order => {
            const {errors} = this.handleIsComplete(order);
            return !errors.some(
                e =>
                    e.category === 'width' ||
                    e.category === 'length' ||
                    e.category === 'depth' ||
                    e.category === 'carton_size'
            );
        };

        canEstimateShipping = order => {
            switch (order.product_type_id) {
                case PRODUCT_TYPE_LABEL:
                    return order.width > 0 && order.length > 0;
                case PRODUCT_TYPE_CARTON:
                    return order.width > 0 && order.length > 0 && order.depth > 0;
                default:
                    break;
            }
        };

        canEstimateDesignDelivery = order => {
            switch (order.product_type_id) {
                case PRODUCT_TYPE_GRAPHIC_DESIGN:
                    return (
                        order.graphic_design_order_details.design_type_id &&
                        order.graphic_design_order_details.design_product_type_id
                    );
                default:
                    return false;
            }
        };

        handleToggleProductType = (productTypeSlug, orderId) => {
            const url = new URL(window.location);
            url.pathname = `orders/${productTypeSlug}`;
            window.history.replaceState({}, '', url);
        };

        handleRemoveProductIdFromUrl = productTypeId => {
            this.handleToggleProductType(this.getProductTypeSlug(productTypeId), '');
        };

        getProductTypeSlug = productTypeId => {
            return productTypeId === PRODUCT_TYPE_LABEL
                ? 'custom-labels'
                : productTypeId === PRODUCT_TYPE_GRAPHIC_DESIGN
                  ? 'custom-design'
                  : 'custom-cartons';
        };

        handleToggleOrder = orderId => {
            this.setState(state => {
                const order = state.cart.orders.find(o => o.id === orderId);

                this.handleUpdateUrl(orderId);

                return {order, showOrder: true};
            });
        };

        /**
         * Update overwrite the url if we change orders
         * @param orderId
         */
        handleUpdateUrl = orderId => {
            const url = new URL(window.location);
            url.pathname = 'orders/custom-labels/' + orderId;
            window.history.replaceState({}, '', url);
        };

        handleAddOrder = () => {
            this.setState(
                state => update(state, {isAddingNewOrder: {$set: true}}),
                async () => {
                    // Fire and forget. Let the debouncer handle the quote update collisions/race conditions
                    const {
                        body: {order, cart}
                    } = await SuperAgent.post('/orders/quoter')
                        .set('accept', 'application/json')
                        .send({order: {}});

                    this.setState({
                        isAddingNewOrder: false,
                        order: this.mapOrderToState(order),
                        cart: this.mapCartToState(cart)
                    });
                }
            );
        };

        debounceQuoteUpdate = async ({skipSendData = false} = {}) => {
            this.nounce = Date.now().toString().substring(-4, 4);
            if (typeof this.request !== 'undefined') {
                this.request.abort();
            }
            let endpoint = '/orders/quoter/';
            if (typeof this.state.order.id !== 'undefined') {
                endpoint += this.state.order.id;
            }
            this.request = SuperAgent.post(endpoint)
                .set('accept', 'application/json')
                .send(skipSendData ? {} : this.mapStateToRequest());

            const request = this.request;
            const localNounce = this.nounce;
            const gen = this.debounceQuoteGenerator(localNounce, request);
            let result = {value: null};
            // eslint-disable-line
            while (true) {
                let value;
                try {
                    value = await result.value;
                } catch (e) {
                    console.log('nounce: ' + localNounce + '-' + e.message); // eslint-disable-line
                    break;
                }
                if (localNounce < this.nounce) {
                    request.abort();
                    break;
                }
                // I don't think this is necessary
                if (!this.shouldSubmitChange(this.state.order)) {
                    break;
                }

                result = gen.next(value);
                if (result.done) {
                    break;
                }
            }
        };

        debounceQuoteGenerator = function* (localNounce, request) {
            yield new Promise(resolve => this.setState({isSaving: true}, resolve));
            yield new Promise(resolve => setTimeout(resolve, 100));
            let response = {};
            try {
                response = yield request;
            } catch (e) {
                console.log(e.message); // eslint-disable-line
            }
            yield new Promise(resolve => {
                this.mapQuoteToState(response);
                resolve();
            });
        };

        /**
         * Sets the order object in the state
         * @param response
         */
        mapQuoteToState(response) {
            this.setState(mapQuoteToState(response.body));
        }

        mapCartToState = cart => {
            return mapCartToState(cart);
        };

        mapOrderToState = order => {
            return mapOrderToState(order);
        };

        mapSubstratesToObject(substrates) {
            let substratesObject = {};

            substrates.map(category => {
                category.substrates.map(substrate => {
                    substratesObject[substrate.id] = substrate;
                });
            });

            return substratesObject;
        }

        /**
         * track what version the order was placed with
         */
        quoterVersionId = () => {
            const baseVersion = '20210301';
            const subVersion = '2';

            const quoterVersionId = [baseVersion, subVersion, 'new'];

            return quoterVersionId.join('-');
        };

        /**
         * Set up the order to be saved
         * Runs when getting a quote or before adding to cart
         */
        mapStateToRequest = () => {
            let {order} = this.state;

            const request = {
                order: {
                    size_id: order.size_id,
                    size_type_id: order.size_type_id,
                    embossing_type_id: order.embossing_type_id,
                    product_type_id: order.product_type_id,
                    shape_id: order.shape_id,
                    width: order.width,
                    length: order.length,
                    depth: order.depth,
                    is_square_corners: Boolean(order.is_square_corners),
                    is_special_roll_qty: Boolean(order.is_special_roll_qty),
                    is_blank: Boolean(order.is_blank),
                    is_embossing: Boolean(order.is_embossing),
                    is_design_in_progress: Boolean(order.is_design_in_progress),
                    is_reverse_print: Boolean(order.is_reverse_print),
                    is_duplex: Boolean(order.is_duplex),
                    is_account_roll_defaults_applied: order.is_account_roll_defaults_applied,
                    is_fewest_possible_rolls: order.is_fewest_possible_rolls,
                    roll_max: order.roll_max,
                    form_factor_id: order.form_factor_id,
                    roll_direction_id: order.roll_direction_id,
                    color_id: order.color_id,
                    duplex_color_id: order.duplex_color_id,
                    substrate_id: order.substrate_id,
                    substrate_backing_id: order.substrate_backing_id,
                    laminate_id: order.laminate_id,
                    is_variable_data: Boolean(order.is_variable_data),
                    is_variable_print_only: Boolean(order.is_variable_print_only),
                    is_printed_proof_only: Boolean(order.is_printed_proof_only),
                    is_flat_printed_proof: Boolean(order.is_flat_printed_proof),
                    is_high_opacity_black: Boolean(order.is_high_opacity_black),
                    is_high_opacity_white: Boolean(order.is_high_opacity_white),
                    variable_data_special_requests: order.variable_data_special_requests,
                    processing_id: order.processing_id,
                    proof_type_id: order.proof_type_id,
                    is_color_matching: order.is_color_matching,
                    pantone_qty: order.pantone_qty,
                    pantone_comments: order.pantone_comments,
                    comments_customer: order.comments_customer,
                    comments_preprinting: order.comments_preprinting,
                    quoter_version_id: this.quoterVersionId(),
                    customer_label: order.customer_label,
                    is_front_back: order.is_front_back,
                    graphic_design_order_details: order.graphic_design_order_details
                }
                // CRUD is handled in their components
                // designs_orders: designs_orders
                // order_uploads: uploads
            };
            return request;
        };

        /**
         * Update the designs_orders
         * @param targetPath
         * @param callback
         */
        handleDesignUpdate = (targetPath, callback) => {
            this.setState(
                state => {
                    return update(state, targetPath);
                },
                () => {
                    this.debounceQuoteUpdate();
                    if (callback) {
                        callback.call();
                    }
                }
            );
        };

        /**
         * Is the order complete?
         * Minimum needed data exists about the order
         * Some bits are conditional
         * Must pass this test in order to add it to the cart
         */
        handleIsComplete = order => {
            return validateOrderWithErrors(order);
        };

        handleAddToCart = () => {
            this.setState(
                {isAddingToCart: true, showErrorsFromCart: false, showAddedToCart: false},
                async () => {
                    try {
                        const {
                            body: {cart, order}
                        } = await SuperAgent.post('/carts/add-to-cart/' + this.state.order.id).set(
                            'accept',
                            'application/json'
                        );

                        if (typeof window.dataLayer !== 'undefined') {
                            window.dataLayer.push({
                                order_id: this.state.order.id,
                                cart_id: this.state.cart.id,
                                gtm_user_id: this.state.order.user_id,
                                event: 'addToCart'
                            });
                        }

                        this.setState(
                            {
                                showAddedToCart: true,
                                lastOrder: {...this.state.order, status_id: STATUS_100},
                                order: this.mapOrderToState(order),
                                cart: this.mapCartToState(cart),
                                size: {},
                                isAddingToCart: false,
                                designQuoterStep: DESIGN_QUOTER_STEP_LANDING
                            },
                            () => {
                                this.handleRemoveProductIdFromUrl(this.state.order.product_type_id);
                                document.body.scrollTop = 0; // For Safari
                                document.documentElement.scrollTop = 0; // For Chrome, Firefox, IE and Opera
                            }
                        );
                    } catch (e) {
                        this.setState({showErrorsFromCart: true, isAddingToCart: false}, () => {
                            document.body.scrollTop = 0; // For Safari
                            document.documentElement.scrollTop = 0; // For Chrome, Firefox, IE and Opera
                        });
                    }
                }
            );
        };

        handleSaveToAccount = order => {
            this.setState(
                {
                    order,
                    showSavedToAccount: true,
                    designQuoterStep: DESIGN_QUOTER_STEP_LANDING
                },
                () => {
                    this.handleRemoveProductIdFromUrl(this.state.order.product_type_id);
                    document.body.scrollTop = 0; // For Safari
                    document.documentElement.scrollTop = 0; // For Chrome, Firefox, IE and Opera
                }
            );
        };

        handleCalcDiameter = qty => {
            const {
                order: {
                    width,
                    length,
                    repeat_length: repeatLength,
                    substrate_id: substrateId,
                    is_high_opacity_white: isHighOpacityWhite
                }
            } = this.state;

            return calcDiameterFromQty({
                qty,
                length,
                orderId,
                substrateId,
                laminateId,
                isHighOpacityWhite
            });
        };

        handleCalcQty = diameter => {
            const {
                order: {
                    id: orderId,
                    width,
                    length,
                    repeat_length: repeatLength,
                    substrate_id: substrateId,
                    laminate_id: laminateId,
                    is_high_opacity_white: isHighOpacityWhite
                }
            } = this.state;
            return calcQtyFromDiameter({
                diameter,
                length,
                orderId,
                substrateId,
                laminateId,
                isHighOpacityWhite
            });
        };

        handleUpdateRollQty = (value, field) => {
            let targetUpdate;
            value = isNaN(value) ? '' : value;

            // Enforce minimum and maximum diameter constraints
            if (field === 'roll_diameter_max' && value > 0) {
                // Apply 4-inch minimum constraint
                value = value < 4 ? 4 : value;

                // Apply width-based maximum constraint
                const maxAllowed = getMaxRollDiameterForWidth(
                    this.state.order.width,
                    this.state.order.product_type_id
                );
                value = value > maxAllowed ? maxAllowed : value;
            }

            // Enforce width-based constraint for roll quantity by checking resulting diameter
            if (field === 'roll_qty_max' && value > 0) {
                const {
                    order: {
                        width,
                        length,
                        substrate_id: substrateId,
                        is_high_opacity_white: isHighOpacityWhite
                    }
                } = this.state;

                // Only calculate if we have valid parameters
                if (length > 0 && substrateId) {
                    // Calculate what diameter this quantity would result in
                    const resultingDiameter = calcDiameterFromQty({
                        qty: value,
                        length,
                        orderId: this.state.order.id,
                        substrateId,
                        laminateId: this.state.order.laminate_id,
                        isHighOpacityWhite
                    });

                    const maxAllowedDiameter = getMaxRollDiameterForWidth(
                        width,
                        this.state.order.product_type_id
                    );

                    // If the resulting diameter exceeds the limit, calculate the max allowed quantity
                    if (resultingDiameter > maxAllowedDiameter) {
                        value = calcQtyFromDiameter({
                            diameter: maxAllowedDiameter,
                            length,
                            orderId: this.state.order.id,
                            substrateId,
                            laminateId: this.state.order.laminate_id,
                            isHighOpacityWhite
                        });
                    }
                }
            }

            targetUpdate = {
                is_special_roll_qty: {$set: true},
                is_account_roll_defaults_applied: {$set: true}
            };
            switch (field) {
                case 'roll_qty_max':
                    targetUpdate = {
                        ...targetUpdate,
                        ...{
                            roll_max: {$set: value === 0 ? '' : value},
                            roll_qty_max: {$set: value === 0 ? '' : value},
                            roll_diameter_max: {$set: ''}
                        }
                    };
                    break;
                case 'roll_diameter_max':
                    targetUpdate = {
                        ...targetUpdate,
                        ...{
                            roll_max: {$set: value === 0 ? '' : value},
                            roll_diameter_max: {$set: value === 0 ? '' : value},
                            roll_qty_max: {$set: ''}
                        }
                    };
                    break;
            }

            this.setState(state => update(state, {order: targetUpdate}), this.debounceQuoteUpdate);
        };

        getDefaultOrderObject = (prevOrder = null) => {
            let order = {
                product_type_id: PRODUCT_TYPE_LABEL
            };
            if (prevOrder) {
                order = {
                    product_type_id: prevOrder.product_type_id
                };
                if (prevOrder.product_type_id === PRODUCT_TYPE_GRAPHIC_DESIGN) {
                    order = {
                        ...order,
                        ...{
                            graphic_design_order_details: {
                                design_type_id: 0,
                                design_product_type_id: PRODUCT_TYPE_LABEL
                            }
                        }
                    };
                }
            }
            return order;
        };

        render() {
            const props = {};

            // We have to pass this for our handle_change HOC
            // We use this quite extensively
            props.onChange = this.handleChange;

            const context = {...this.state};

            context.handleUpdate = this.handleChange;
            context.handleChange = this.handleChange;
            context.handleToggleProductType = this.handleToggleProductType;
            context.handleRemoveProductIdFromUrl = this.handleRemoveProductIdFromUrl;
            context.getProductTypeSlug = this.getProductTypeSlug;
            context.handleToggleModal = key => {
                this.handleChange({modalKey: {$set: key}});
            };
            context.handleToggleOrder = this.handleToggleOrder;
            context.handleAddOrder = this.handleAddOrder;
            context.handleUpdateUrl = this.handleUpdateUrl;
            context.handleIsComplete = this.handleIsComplete;
            context.handleDebounceQuoteUpdate = this.debounceQuoteUpdate;
            context.handleMapOrderToState = this.mapOrderToState;
            context.handleMapCartToState = this.mapCartToState;
            context.handleAddToCart = this.handleAddToCart;
            context.handleSaveToAccount = this.handleSaveToAccount;

            context.handleSaveToAccount = this.handleSaveToAccount;
            context.handleUpdateRollQty = this.handleUpdateRollQty;
            context.handleChangeMaterial = this.handleChangeMaterial;

            context.canEstimateShipping = this.canEstimateShipping;
            context.canEstimateDesignDelivery = this.canEstimateDesignDelivery;
            context.singularProduct = 'label';
            context.pluralizedProduct = 'labels';

            context.handleDesignOrderChange = this.handleDesignOrderChange;
            context.handleSetDesignQuoterStep = this.handleSetDesignQuoterStep;

            switch (context.order.product_type_id) {
                case PRODUCT_TYPE_LABEL:
                    context.singularProduct = 'label';
                    context.pluralProduct = 'labels';
                    break;
                case PRODUCT_TYPE_CARTON:
                    context.singularProduct = 'carton';
                    context.pluralProduct = 'cartons';
                    break;
                case PRODUCT_TYPE_GRAPHIC_DESIGN:
                    context.designOrder = this.state.order.graphic_design_order_details;

                    switch (this.state.order.graphic_design_order_details?.design_product_type_id) {
                        case PRODUCT_TYPE_LABEL:
                            context.singularProduct = 'label';
                            context.pluralProduct = 'labels';
                            break;
                        case PRODUCT_TYPE_CARTON:
                            context.singularProduct = 'carton';
                            context.pluralProduct = 'cartons';
                            break;
                    }
            }

            // Pass the orderId as a prop because changing it will need to update sub lists like
            // designsOrders and pantones
            return (
                <QuoteContext.Provider value={context}>
                    <ContainedComponent
                        {...props}
                        orderId={this.state.order.id}
                        isReorder={this.state.order.is_reorder}
                    />
                </QuoteContext.Provider>
            );
        }
    };
};
